import { useTheme } from '@emotion/react'
import { TheWestSection } from '@news-mono/common'
import {
    BreachEvent,
    DataLayerEventName,
    useToggleState,
    ViewSubscriptionItem,
} from '@news-mono/web-common'
import {
    TheWestRegionalSections,
    TheWestSections,
} from '@west-australian-newspapers/publication-types'
import React, { useCallback, useEffect } from 'react'
import {
    StyledBreachWallContent2022,
    StyledRegistrationInfo,
    StyledPackagesContainer,
    BreachWallWrapper,
    StyledFooterAndTermsContainer,
} from './BreachWallContent.styled'
import {
    BreachWallContentType,
    BreachWallStyle,
    mapSubscriptionPackagesToFlatList,
} from '../../helpers/BreachWallTypes'
import { useSection } from '../../../../__helpers'
import { getAttribution } from '../../../../errors/ErrorPage'
import { toItemListId, DEFAULT_CART_QUANTITY } from '../../../../tracking'
import { SubscriptionPackages } from '../../common'
import SubscriptionPackage from '../SubscriptionPackage/SubscriptionPackage'

export interface BreachWallContentProps {
    title: () => JSX.Element | null
    terms: () => JSX.Element | null
    footer: () => JSX.Element | null
    mediaBanner: () => JSX.Element | null
    breachBanner: () => JSX.Element | null
    subscriptionPackages?: SubscriptionPackages
    // Values passed down from the BreachWallContentWrapper in relation to the breach
    breachCreativeName: string
    breachType: BreachWallContentType
    breachDeal: string
    breachStyle: BreachWallStyle
    onEvent: (event: BreachEvent) => void
    // Values passed down if it's a test BreachWallContent
    isTest: boolean | undefined
    isRegionalOverride: boolean
}

const backgroundImagePrefix = './assets/thewest-digital-hero-variation-'
export const BreachWallContent: React.FC<BreachWallContentProps> = ({
    mediaBanner: renderMediaBanner,
    breachBanner: renderBanner,
    terms: renderTerms,
    title: renderTitleSection,
    footer: renderFooter,
    subscriptionPackages,
    breachCreativeName,
    breachType,
    breachDeal,
    breachStyle,
    onEvent,
    isTest,
    isRegionalOverride,
}) => {
    const theme = useTheme()
    const masthead =
        getAttribution(theme.kind, theme.section) || 'The West Australian'
    const section = useSection<TheWestSection>()

    const isRegional = !isRegionalOverride && section in TheWestRegionalSections

    const backgroundImageVariation = useBackgroundImageVariation(breachStyle)
    const showBreachBanner = canShowBreachBanner(breachStyle) && !isRegional

    let largeImage = undefined
    let smallImage = undefined

    // Assign background image if utilising new breach design.
    switch (breachType) {
        case 'black-friday':
            largeImage = require(`${backgroundImagePrefix}${backgroundImageVariation}.jpg`)
            smallImage = require(`${backgroundImagePrefix}${backgroundImageVariation}-cropped.jpg`)
            break
        case '50-off-afl-breach-screen':
        case 'subs-campaign-eofy-weekend-digital':
            largeImage = require(`${backgroundImagePrefix}${backgroundImageVariation}.jpg`)
            smallImage = require(`${backgroundImagePrefix}${backgroundImageVariation}-mobile.jpg`)
            break
    }

    // when everything is loaded, fire off a view_item_list GA4 event
    useEffect(() => {
        const availableDeal = breachDeal === '' ? 'default' : breachDeal
        const breachAvailablePayload: BreachEvent['payload'] = {
            paywallType: 'freemium',
            leadDepth: 0,
            creative: breachCreativeName,
            deal: availableDeal,
            breachScreenType: 'Premium - Inline',
            popupMethod: 'auto',
            pricepoint: [7, 8],
        }
        onEvent({
            type: DataLayerEventName.breachScreenAvailable,
            originator: 'breachscreen',
            payload: breachAvailablePayload,
        })

        const flatSubscriptions = subscriptionPackages
            ? mapSubscriptionPackagesToFlatList(subscriptionPackages)
            : undefined
        const viewSubscriptionItems: ViewSubscriptionItem[] =
            flatSubscriptions?.map((pkg, index) => ({
                index,
                deal_name: breachType,
                item_name: pkg.heading,
                item_id: pkg.rateCode,
                item_brand: masthead,
                component_path: pkg.packagePath,
                item_list_category: 'freemium',
                item_list_category2: 'Premium - Inline',
                item_list_name: breachCreativeName,
                item_list_id: toItemListId(breachCreativeName),
                price: pkg.weeklyPrice,
                promo_price: pkg.promoPrice || 0,
                discount: pkg.discount || 0,
                coupon: pkg.offerCode || '',
                quantity: DEFAULT_CART_QUANTITY,
                currency: 'AUD',
            })) || []
        onEvent({
            type: DataLayerEventName.viewSubscriptionItemListEvent,
            parentType: DataLayerEventName.viewItemListEvent,
            originator: 'breachscreen',
            payload: { items: viewSubscriptionItems },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const isFullWidthBelowDesktop = breachStyle === 'eofy'
    const TitleComponent = useCallback(
        () => renderTitleSection(),
        [renderTitleSection],
    )()

    // New 2024 Optimizely Breachwall design
    return (
        <BreachWallWrapper
            isFullWidthBelowDesktop={isFullWidthBelowDesktop}
            breachType={breachType}
        >
            <StyledBreachWallContent2022
                backgroundColor={'#F4F5F5'}
                largeImage={largeImage}
                smallImage={smallImage}
                breachStyle={breachStyle}
            >
                {/* NOTE: DO NOT HARD CODE ANYTHING HERE UNLESS ABSOLUTELY NECESSARY, USE THE PROP RENDER METHODS */}
                {renderMediaBanner()}
                {breachStyle != 'default-breachwall-2024' ? (
                    <StyledRegistrationInfo>
                        {breachStyle && TitleComponent}
                    </StyledRegistrationInfo>
                ) : (
                    TitleComponent
                )}
                {/* NOTE: DO NOT HARD CODE ANYTHING HERE UNLESS ABSOLUTELY NECESSARY, USE THE PROP RENDER METHODS */}
                <StyledPackagesContainer breachType={breachType}>
                    {subscriptionPackages &&
                        subscriptionPackages.map(
                            (subscriptionPackageItem, index) => (
                                <SubscriptionPackage
                                    key={index}
                                    breachCreativeName={breachCreativeName}
                                    breachDeal={breachDeal}
                                    breachStyle={breachStyle}
                                    breachContent={
                                        showBreachBanner
                                            ? breachType
                                            : undefined
                                    }
                                    onEvent={onEvent}
                                    packageInfo={subscriptionPackageItem}
                                    masthead={masthead}
                                    isRegionalOverride={isRegionalOverride}
                                />
                            ),
                        )}
                </StyledPackagesContainer>
                {/* NOTE: DO NOT HARD CODE ANYTHING HERE UNLESS ABSOLUTELY NECESSARY, USE THE PROP RENDER METHODS */}
                <StyledFooterAndTermsContainer breachType={breachType}>
                    {renderFooter()}
                    {renderTerms()}
                    {renderBanner()}
                </StyledFooterAndTermsContainer>
            </StyledBreachWallContent2022>
        </BreachWallWrapper>
    )
}

function useBackgroundImageVariation(breachStyle: BreachWallStyle) {
    const backgroundVariation =
        useToggleState()?.['tp-subscription-hero-variation'] || 'a'

    // check for the background variation against the breachStyle
    switch (breachStyle) {
        case 'vanishing-cousins':
            return 'vanishing-cousins'
        case 'black-friday':
            return 'black-friday'
        case 'eofy':
            return 'eofy'
        case 'afl50':
            return 'afl50'
    }

    return backgroundVariation
}

function canShowBreachBanner(breachStyle: BreachWallStyle) {
    if (
        breachStyle === 'black-friday' ||
        breachStyle === 'eofy' ||
        breachStyle === 'afl50' ||
        breachStyle !== 'default-breachwall-2024'
    ) {
        return true
    }
    return false
}

BreachWallContent.displayName = 'BreachWallContent'
export default BreachWallContent
